import { useState, useEffect } from 'react';
import { Box, Button, useTheme, ClickAwayListener } from '@mui/material';
import moment from 'moment';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CalendarMonth from '@mui/icons-material/CalendarMonth';
import ArrowBack from '@mui/icons-material/ArrowBack';

import { format } from 'date-fns';

import { registerLocale } from 'react-datepicker';
import es from 'date-fns/locale/es';
import './customGitDatePicker.css';

const esCapitalized = {
  ...es,
  localize: {
    ...es.localize,
    month: (monthIndex, options) => {
      const month = es.localize.month(monthIndex, options);
      return month.charAt(0).toUpperCase() + month.slice(1);
    },
  },
};

registerLocale('es', esCapitalized);

const Calendar = ({
  month,
  year,
  startDate,
  endDate,
  selectCalendarDay,
  mobile,
  minDateParam,
  maxDateParam,
  arrayDates,
}) => {
  const mounthNames = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ];
  const weekDays = ['DO', 'LU', 'MA', 'MI', 'JU', 'VI', 'SA'];
  const theme = useTheme();

  let minDate;
  let maxDate;

  minDate = minDateParam ? new Date(minDateParam.toDateString()) : new Date();
  if (!minDateParam) {
    minDate.setHours(0, 0, 0, 0);
    minDate.setSeconds(minDate.getSeconds() - 1);
  } else {
    minDate.setDate(minDate.getDate() - 1);
  }
  maxDate = maxDateParam ?? new Date();
  if (!maxDateParam) maxDate.setFullYear(maxDate.getFullYear() + 1);

  useEffect(() => {
    minDate = minDateParam ? new Date(minDateParam.toDateString()) : new Date();
    if (!minDateParam) {
      minDate.setHours(0, 0, 0, 0);
      minDate.setSeconds(minDate.getSeconds() - 1);
    } else {
      minDate.setDate(minDate.getDate() - 1);
    }
    maxDate = maxDateParam ?? new Date();
    if (!maxDateParam) maxDate.setFullYear(maxDate.getFullYear() + 1);
  }, [minDateParam, maxDateParam]);

  const startDayFn = (month, year) => {
    const date = new Date(year, month, 1);
    let day = date.getDay(); // 0 (Domingo) a 6 (Sábado)
    //day = (day === 0) ? 6 : day - 1; // Ajustar para que Lunes sea 0
    return day;
  };

  const amountOfDays = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getTypeOfDay = (day, month, year, minDate, maxDate, arrayDates) => {
    const date = new Date(year, month, day);
    let filtred = null;
    if (arrayDates) {
      filtred = arrayDates.find(d => {
        return d.date === format(date, 'yyyy-MM-dd');
      });
    }
    if (date <= minDate || date > maxDate) {
      return filtred ? 7 : 1;
    }
    if (
      startDate &&
      date.toDateString() === startDate.toDateString() &&
      !endDate
    ) {
      return 2;
    }
    if (startDate && date.toDateString() === startDate.toDateString()) {
      return 3;
    }
    if (endDate && date.toDateString() === endDate.toDateString()) {
      return 4;
    }
    if (startDate && endDate && date > startDate && date < endDate) {
      return 5;
    }
    if (filtred) {
      return 6;
    }
    return 0;
  };

  const isRangeBackground = (day, month, year, minDate, maxDate) => {
    const typeDay = getTypeOfDay(day, month, year, minDate, maxDate);
    return typeDay === 5;
  };

  const isPrimaryBackground = (day, month, year, minDate, maxDate) => {
    const typeDay = getTypeOfDay(day, month, year, minDate, maxDate);
    return typeDay === 2 || typeDay === 3 || typeDay === 4;
  };

  const getClassForDay = (
    day,
    month,
    year,
    minDate,
    maxDate,
    mobile,
    arrayDates
  ) => {
    let mobileCss = mobile ? ' mobileCalendarDay ' : '';
    const typeDay = getTypeOfDay(
      day,
      month,
      year,
      minDate,
      maxDate,
      arrayDates
    );
    switch (typeDay) {
      case 1:
        return mobileCss + 'calendarDay disabledDay';
      case 2:
        return mobileCss + 'calendarDay selectedDayUnitary';
      case 3:
        return mobileCss + 'calendarDay selectedDayLimitLeft';
      case 4:
        return mobileCss + 'calendarDay selectedDayLimitRight';
      case 5:
        return mobileCss + 'calendarDay inRangeDay';
      case 6:
        return mobileCss + 'calendarDay secundarySelectedDayUnitary';
      case 7:
        return (
          mobileCss + 'calendarDay disabledDay secundarySelectedDayUnitary'
        );
      default:
        return mobileCss + 'calendarDay';
    }
  };

  const days = amountOfDays(month, year);

  const startDay = startDayFn(month, year);

  return (
    <div style={{ flex: 1 }}>
      <h3 className="h3mgt calendarMounth">
        <span style={{ color: '#000' }}>{mounthNames[month]}</span>
        <span
          style={{
            fontWeight: '600',
            fontSize: '15px',
            marginLeft: '6px',
            color: '#222222ff',
          }}
        >
          {year}
        </span>
      </h3>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '0px',
        }}
      >
        {/* Días de la semana */}
        {weekDays.map(day => (
          <div
            key={day}
            style={{
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '10px',
              marginBottom: '8px',
              marginTop: '2px',
              color: 'gray',
            }}
            className="disableSelect"
          >
            {day}
          </div>
        ))}

        {/* Espacios vacíos antes del día 1 */}
        {Array.from({ length: startDay }).map((_, i) => (
          <div key={`empty-${i}`} />
        ))}

        {/* Días del mes */}
        {Array.from({ length: days }).map((_, i) => (
          <Box
            key={i}
            className={getClassForDay(
              i + 1,
              month,
              year,
              minDate,
              maxDate,
              mobile,
              arrayDates
            )}
            onClick={() =>
              selectCalendarDay(new Date(year, month, i + 1), minDate, maxDate)
            }
            sx={{
              ...(isPrimaryBackground(i + 1, month, year, minDate, maxDate)
                ? {
                    backgroundColor: theme.primary,
                    color: '#000',
                  }
                : isRangeBackground(i + 1, month, year, minDate, maxDate)
                ? {
                    backgroundColor: theme.primaryLight,
                  }
                : {}),
              '&:hover': {
                backgroundColor: theme.primary + '!important',
                color: '#fff!important',
              },
            }}
          >
            {i + 1}
          </Box>
        ))}
      </div>
    </div>
  );
};

// function diffDaysIgnoringTime(date1, date2) {
//   const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
//   const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());

//   const msPerDay = 1000 * 60 * 60 * 24;
//   return Math.abs(Math.round((d2 - d1) / msPerDay));
// }

const DateRangeGit = ({
  checkInParam, //default value from
  checkOutParam, //default value to
  dateInfo, //fn set Date
  placeholders, //names input from/to
  singleSelect, //single calendar
  datePicked, //preselect Date
  arrayDates, //Multidest
  index, //position in arrayMultidest
}) => {
  const theme = useTheme();

  const [minD, setMinD] = useState(null);
  const [maxD, setMaxD] = useState(null);

  useEffect(() => {
    if (singleSelect) {
      let min = null;
      let max = null;
      if (arrayDates)
        for (let i = 0; i < arrayDates.length; i++) {
          if (i < index) {
            //set min
            if (arrayDates[i].date && arrayDates[i].date != null) {
              if (
                new Date(arrayDates[i].date + 'T12:00') > new Date(min) ||
                min === null
              )
                min = new Date(arrayDates[i].date + 'T12:00');
            }
          } else if (i > index) {
            //set max
            if (arrayDates[i].date && arrayDates[i].date != null) {
              if (
                new Date(arrayDates[i].date + 'T12:00') < new Date(max) ||
                max === null
              )
                max = new Date(arrayDates[i].date + 'T12:00');
            }
          }
        }
      if (min !== null) {
        setMinD(min);
        setSelectedMonth({
          month: min.getMonth(),
          year: min.getFullYear(),
        });
      }
      if (max !== null) {
        setMaxD(max);
      }
    }
  }, [index, arrayDates, singleSelect]);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [count, setCount] = useState(0);

  const [hidden, setHiddenOrg] = useState(true);

  const [selectedMonth, setSelectedMonth] = useState({
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
  });

  const setHidden = value => {
    if (value) {
      if (startDate) {
        setSelectedMonth({
          month: startDate.getMonth(),
          year: startDate.getFullYear(),
        });
      } else if (minD) {
        setSelectedMonth({
          month: minD.getMonth(),
          year: minD.getFullYear(),
        });
      }
    }
    setHiddenOrg(value);
  };

  const currentMonth = {
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
  };

  useEffect(() => {
    setStartDate(
      singleSelect
        ? datePicked
          ? moment(datePicked, 'YYYY-MM-DD').toDate()
          : null
        : checkInParam
        ? moment(checkInParam, 'YYYY-MM-DD').toDate()
        : null
    );
    setEndDate(
      checkOutParam ? moment(checkOutParam, 'YYYY-MM-DD').toDate() : null
    );

    if (index === 0 || index) {
      if (!window.mopc) window.mopc = {};
      window.mopc[index] = () => {
        setHidden(false);
      };
    } else {
      window.opc = () => {
        if (!index) {
          setHidden(false);
        }
      };
    }
    if (checkInParam) {
      setSelectedMonth({
        month: moment(checkInParam, 'YYYY-MM-DD').toDate().getMonth(),
        year: moment(checkInParam, 'YYYY-MM-DD').toDate().getFullYear(),
      });
    }
  }, [checkInParam, checkOutParam]);

  const calcNights = (start, end) => {
    if (!start || !end) return 0;
    return Math.floor((end - start) / (1000 * 60 * 60 * 24));
  };

  const emitDateInfo = (start, end) => {
    dateInfo({
      check_in: start ? moment(start).format('YYYY-MM-DD') : null,
      check_out: end ? moment(end).format('YYYY-MM-DD') : null,
      total_nights: calcNights(start, end),
    });
  };

  const [amountOfDays, setAmountOfDays] = useState(null);

  const selectCalendarDay = (day, minDate, maxDate) => {
    if (singleSelect) {
      if (day <= minDate || day > maxDate) {
        return;
      }
      setStartDate(day);

      const iso = format(day, 'yyyy-MM-dd');
      dateInfo({ date_selected: iso });

      setHidden(true);
      setIgnoreClose(true);
    } else {
      if (day <= minDate || day > maxDate) {
        return;
      }

      const originalVal = {
        startDate,
        endDate,
        setFrom: true,
      };

      // const difFrom = !startDate ? null : diffDaysIgnoringTime(day, startDate);
      // const difTo = !endDate ? null : diffDaysIgnoringTime(day, endDate);

      // Lógica del más cercano
      // if (startDate && endDate && difFrom != difTo && difTo != null) {
      //   if (difTo < difFrom) {
      //     setEndDate(day);
      //     originalVal.setFrom = false;
      //   } else {
      //     setStartDate(day);
      //     originalVal.setFrom = true;
      //   }
      // } else {
      //   if (count % 2 === 0) {
      //     //select from
      //     if (endDate && day > endDate) {
      //       setEndDate(day);
      //       originalVal.setFrom = false;
      //     } else {
      //       setStartDate(day);
      //       setCount(count + 1);
      //       originalVal.setFrom = true;
      //     }
      //   } else {
      //     //select to
      //     if (day < startDate) {
      //       setStartDate(day);
      //       originalVal.setFrom = true;
      //     } else {
      //       setEndDate(day);
      //       setCount(count + 1);
      //       originalVal.setFrom = false;
      //     }
      //   }
      // }

      // Lógica del calendario anterior
      if (count % 2 === 0) {
        //select from
        if (endDate && day > endDate) {
          setTimeout(() => {
            setEndDate(null);
          }, 10);
        }
        setStartDate(day);
        setCount(count + 1);
        originalVal.setFrom = true;
      } else {
        //select to
        if (day < startDate) {
          setTimeout(() => {
            setStartDate(null);
          }, 10);
        }
        setEndDate(day);
        setCount(count + 1);
        originalVal.setFrom = false;
      }

      if (
        (startDate && endDate) ||
        (originalVal.setFrom && endDate) ||
        (!originalVal.setFrom && startDate)
      ) {
        setAmountOfDays(
          calcNights(
            originalVal.setFrom ? day : startDate,
            !originalVal.setFrom ? day : endDate
          )
        );
        emitDateInfo(
          originalVal.setFrom ? day : startDate,
          !originalVal.setFrom ? day : endDate
        );
      } else {
        setAmountOfDays(null);
      }
    }
  };

  const getDayDescription = date => {
    //Ju 10 FEB 2026
    const res = moment(date).format('dd DD MMM YYYY').toUpperCase();
    return (res[0] + res[1].toLocaleLowerCase() + ' ' + res.slice(3)).replace(
      /\./g,
      ''
    );
  };

  const cleanDates = () => {
    setStartDate(null);
    setEndDate(null);
    emitDateInfo(null, null);
    setAmountOfDays(null);
    setCount(0);
  };

  const goToPreviousOrNextMonth = next => {
    if (next) {
      if (
        selectedMonth.month == new Date().getMonth() &&
        selectedMonth.year == new Date().getFullYear() + 1
      )
        return;
    } else {
      if (
        selectedMonth.month == new Date().getMonth() &&
        selectedMonth.year == new Date().getFullYear()
      )
        return;
    }

    let newMonth = selectedMonth.month + (next ? 1 : -1);
    let newYear = selectedMonth.year;

    if (newMonth < 0) {
      newMonth = 11;
      newYear -= 1;
    } else if (newMonth > 11) {
      newMonth = 0;
      newYear += 1;
    }

    setSelectedMonth({ month: newMonth, year: newYear });
  };

  const [ignoreClose, setIgnoreClose] = useState(true);

  const isVisible = elem => {
    return !!(elem && elem.offsetWidth > 0 && elem.offsetHeight > 0);
  };

  const handleClickAway = () => {
    if (isVisible(window.document.getElementById('mobcalendarh'))) return;
    if (
      !isVisible(
        window.document.getElementById('webcalendarh' + (index ? index : ''))
      )
    )
      return;

    if (ignoreClose) {
      setIgnoreClose(false);
      return;
    }
    if (!hidden) {
      setHidden(true);
      setIgnoreClose(true);
    }
  };

  return (
    <Box>
      <div
        style={{
          textAlign: 'center',
          minHeight: '50px',
        }}
      >
        <Box
          display="flex"
          gap={1}
          width="100%"
          sx={{
            '&:hover': {
              border: '1px solid ' + theme.primary,
            },
            borderRadius: '8px',
            border: '1px solid #ccc',
          }}
        >
          <div className="calendarContainer">
            <Box
              display="flex"
              gap={1}
              width="100%"
              style={{ maxHeight: '50px' }}
            >
              <Box flex={0} style={{ marginTop: '7px' }}>
                <CalendarMonth
                  style={{
                    ...(!hidden && count % 2 === 0
                      ? { color: hidden ? '#666' : theme.primary }
                      : {}),
                  }}
                />
              </Box>
              <Box
                flex={1}
                style={{
                  fontFamily: 'Open Sans, sans-serif',
                  ...(!hidden && count % 2 === 0
                    ? { fontWeight: 'bold', color: theme.primary }
                    : {}),
                }}
                onClick={() => {
                  if (hidden) {
                    if (!hidden) setIgnoreClose(true);
                    setHidden(!hidden);
                    setCount(0);
                  } else {
                    if (count % 2 === 1) {
                      setCount(0);
                    } else {
                      if (!hidden) setIgnoreClose(true);
                      setHidden(!hidden);
                    }
                  }
                }}
              >
                {startDate
                  ? moment(startDate, 'YYYY-MM-DD').format('DD/MM/YYYY')
                  : placeholders?.startDate ?? 'Salida'}
              </Box>
              {singleSelect ? (
                ''
              ) : (
                <>
                  <Box
                    flex={0}
                    style={{
                      marginTop: '7px',
                      position: 'relative',
                      left: '-8px',
                      borderLeft: '#66666657 solid 1px',
                      paddingLeft: '8px',
                    }}
                  >
                    <CalendarMonth
                      style={{
                        ...(!hidden && count % 2 === 1
                          ? { color: hidden ? '#666' : theme.primary }
                          : {}),
                      }}
                    />
                  </Box>
                  <Box
                    flex={1}
                    style={{
                      fontFamily: 'Open Sans, sans-serif',
                      position: 'relative',
                      left: '-8px',
                      ...(!hidden && count % 2 === 1
                        ? { fontWeight: 'bold', color: theme.primary }
                        : {}),
                    }}
                    onClick={() => {
                      if (hidden) {
                        if (!hidden) setIgnoreClose(true);
                        setHidden(!hidden);
                        setCount(1);
                      } else {
                        if (count % 2 === 0) {
                          setCount(1);
                        } else {
                          if (!hidden) setIgnoreClose(true);
                          setHidden(!hidden);
                        }
                      }
                    }}
                  >
                    {endDate
                      ? moment(endDate, 'YYYY-MM-DD').format('DD/MM/YYYY')
                      : placeholders?.endDate ?? 'Regreso'}
                  </Box>
                </>
              )}

              <Box
                className="mobileElement mobileCalendarContainer"
                flex={1}
                style={{ display: hidden ? 'none' : '' }}
              >
                <Box
                  display="flex"
                  id="mobcalendarh"
                  gap={1}
                  width="100%"
                  className="mobileCalendarHeader"
                >
                  <Box flex={1} style={{ marginTop: '6px' }}>
                    <ArrowBack
                      onClick={() => {
                        setIgnoreClose(true);
                        setHidden(true);
                      }}
                    />
                  </Box>
                  <Box
                    flex={5}
                    style={{
                      fontFamily: 'Open Sans, sans-serif',
                      ...(!hidden && count % 2 === 0
                        ? { fontWeight: 'bold', color: theme.primary }
                        : {}),
                    }}
                    onClick={() => {
                      if (hidden) {
                        setHidden(!hidden);
                        setCount(0);
                      } else {
                        if (count % 2 === 1) {
                          setCount(0);
                        } else {
                          if (!hidden) setIgnoreClose(true);
                          setHidden(!hidden);
                        }
                      }
                    }}
                  >
                    {startDate
                      ? moment(startDate, 'YYYY-MM-DD').format('DD/MM/YYYY')
                      : placeholders?.startDate ?? 'Salida'}
                  </Box>
                  {singleSelect ? (
                    ''
                  ) : (
                    <Box
                      flex={5}
                      style={{
                        borderLeft: '#66666657 solid 1px',
                        paddingLeft: '8px',
                        fontFamily: 'Open Sans, sans-serif',
                        ...(!hidden && count % 2 === 1
                          ? { fontWeight: 'bold', color: theme.primary }
                          : {}),
                      }}
                      onClick={() => {
                        if (hidden) {
                          setHidden(!hidden);
                          setCount(1);
                        } else {
                          if (count % 2 === 0) {
                            setCount(1);
                          } else {
                            setHidden(!hidden);
                          }
                        }
                      }}
                    >
                      {endDate
                        ? moment(endDate, 'YYYY-MM-DD').format('DD/MM/YYYY')
                        : placeholders?.endDate ?? 'Regreso'}
                    </Box>
                  )}
                </Box>
                {singleSelect ? (
                  ''
                ) : (
                  <>
                    <Box
                      display="flex"
                      gap={1}
                      width="100%"
                      className="mobileCalendarFooter"
                      justifyContent={'flex-end'}
                    >
                      {amountOfDays ? (
                        <span
                          style={{
                            marginRight: 'auto',
                            marginTop: '2px',
                            fontWeight: '600',
                            fontSize: '12px',
                            textAlign: 'left',
                            color: '#5a5a5a',
                            position: 'inline-grid',
                            fontFamily: 'Open Sans, sans-serif',
                          }}
                        >
                          <Box display="flex" gap={1} flexWrap="wrap">
                            <Box width="100%" style={{ lineHeight: '1' }}>
                              <span
                                style={{ fontWeight: 'bold', color: '#000' }}
                              >
                                Desde:{' '}
                              </span>
                              {getDayDescription(startDate)} /
                              <span
                                style={{
                                  fontWeight: 'bold',
                                  marginLeft: 4,
                                  color: '#000',
                                }}
                              >
                                Hasta:{' '}
                              </span>
                              {getDayDescription(endDate)}
                            </Box>

                            <Box width="100%" style={{ lineHeight: '1' }}>
                              <span
                                style={{ fontWeight: 'bold', color: '#000' }}
                              >
                                {`Día${amountOfDays + 1 > 1 ? 's' : ''}:`}
                              </span>
                              <span style={{ marginLeft: 4 }}>
                                {`${amountOfDays + 1} / `}
                              </span>
                              <span
                                style={{ fontWeight: 'bold', color: '#000' }}
                              >
                                {`Noche${amountOfDays + 1 > 1 ? 's' : ''}:`}
                              </span>
                              <span style={{ marginLeft: 4 }}>
                                {amountOfDays}
                              </span>
                            </Box>
                          </Box>
                        </span>
                      ) : (
                        ''
                      )}
                      <Button
                        style={{
                          backgroundColor: '#fff',
                          color: '#000',
                          border: '1px solid #ccc',
                        }}
                        onClick={cleanDates}
                      >
                        Limpiar
                      </Button>
                      <Button
                        style={{
                          backgroundColor: theme.primary,
                          color: '#fff',
                          marginRight: '50px',
                          marginLeft: '20px',
                        }}
                        onClick={() => {
                          setIgnoreClose(true);
                          setHidden(true);
                          emitDateInfo(startDate, endDate);
                        }}
                      >
                        Aplicar
                      </Button>
                    </Box>
                  </>
                )}

                <div
                  style={{
                    fontFamily: "'Open Sans', sans-serif",
                    padding: '10px',
                    paddingLeft: '30px',
                    paddingRight: '30px',
                    marginTop: '55px',
                  }}
                >
                  {Array.from({ length: 13 }).map((_, index) => (
                    <Calendar
                      key={'key-calendar' + index}
                      month={
                        currentMonth.month + index > 11
                          ? (currentMonth.month + index) % 12
                          : currentMonth.month + index
                      }
                      year={
                        currentMonth.month + index > 11
                          ? currentMonth.year + 1
                          : currentMonth.year
                      }
                      startDate={startDate}
                      endDate={endDate}
                      selectCalendarDay={selectCalendarDay}
                      mobile={true}
                    />
                  ))}
                  {/* <Calendar month={selectedMonth.month} year={selectedMonth.year} startDate={startDate} endDate={endDate} selectCalendarDay={selectCalendarDay} mobile={true} />
                  <Calendar month={selectedMonth.month == 11 ? 0 : selectedMonth.month + 1} year={selectedMonth.month == 11 ? selectedMonth.year+1 : selectedMonth.year} startDate={startDate} endDate={endDate} selectCalendarDay={selectCalendarDay} mobile={true} /> */}
                </div>
              </Box>
            </Box>
          </div>
        </Box>
        <div style={{ width: '100%', position: 'relative' }}>
          <ClickAwayListener onClickAway={handleClickAway}>
            <div
              id={'webcalendarh' + (index ? index : '')}
              style={{
                width: '100%',
                position: 'absolute',
                left: 0,
                right: 0,
                //minHeight: "320px",
                backgroundColor: 'rgb(255 255 255)',
                zIndex: 901,
                padding: '10px',
                //paddingRight: "20px",
                //paddingLeft: "20px",
                boxSizing: 'border-box',
                fontFamily: "'Open Sans', sans-serif",
                display: 'flex',
                flexDirection: 'column',
                paddingTop: '0px',
                minWidth: singleSelect ? '300px' : '480px',
                boxShadow: '0px 2px 3px rgb(0 0 0 / 28%)',
                ...(hidden ? { display: 'none' } : {}),
              }}
              className="pcElement"
            >
              <div
                style={{
                  display: 'flex',
                  gap: '16px',
                  width: '100%',
                  flex: 1,
                  position: 'absolute',
                }}
              >
                <button
                  style={{
                    padding: '4px 6px',
                    border: 'none',
                    backgroundColor: '#fff',
                    color: '#000',
                    cursor: 'pointer',
                    borderRadius: '20px',
                    position: 'absolute',
                    //left: "-20px",
                    //top: "170px",
                    left: '-10px',
                    top: '8px',
                  }}
                  onClick={() => goToPreviousOrNextMonth(false)}
                >
                  <ChevronLeftIcon />
                </button>
                <button
                  style={{
                    padding: '4px 6px',
                    border: 'none',
                    backgroundColor: '#fff',
                    color: '#000',
                    cursor: 'pointer',
                    borderRadius: '20px',
                    position: 'absolute',
                    //right: "-20px",
                    //top: "170px",
                    right: '20px',
                    top: '8px',
                  }}
                  onClick={() => goToPreviousOrNextMonth(true)}
                >
                  <ChevronRightIcon />
                </button>
              </div>

              {/* Calendarios */}
              <div
                style={{
                  display: 'flex',
                  gap: '24px',
                  width: '100%',
                  flex: 1,
                  ...(singleSelect ? { marginBottom: '10px' } : {}),
                }}
              >
                <Calendar
                  month={selectedMonth.month}
                  year={selectedMonth.year}
                  startDate={startDate}
                  endDate={endDate}
                  selectCalendarDay={selectCalendarDay}
                  minDateParam={minD}
                  maxDateParam={maxD}
                  arrayDates={arrayDates}
                />
                {singleSelect ? (
                  ''
                ) : (
                  <Calendar
                    month={
                      selectedMonth.month == 11 ? 0 : selectedMonth.month + 1
                    }
                    year={
                      selectedMonth.month == 11
                        ? selectedMonth.year + 1
                        : selectedMonth.year
                    }
                    startDate={startDate}
                    endDate={endDate}
                    selectCalendarDay={selectCalendarDay}
                    arrayDates={arrayDates}
                  />
                )}
              </div>

              {/* Fila de botones */}
              {singleSelect ? (
                ''
              ) : (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '12px',
                    marginTop: '8px',
                    borderTop: '1px solid #eee',
                    paddingTop: '8px',
                  }}
                >
                  {amountOfDays ? (
                    <span
                      style={{
                        marginRight: 'auto',
                        marginTop: '2px',
                        fontWeight: '600',
                        fontSize: '11px',
                        textAlign: 'left',
                        color: '#5a5a5a',
                      }}
                    >
                      <span>
                        <span style={{ fontWeight: 'bold', color: '#000' }}>
                          Desde:{' '}
                        </span>
                        {getDayDescription(startDate)} /
                        <span
                          style={{
                            fontWeight: 'bold',
                            marginLeft: '4px',
                            color: '#000',
                          }}
                        >
                          Hasta:{' '}
                        </span>
                        {getDayDescription(endDate)}
                      </span>
                      <br />
                      <span>
                        <span
                          style={{ fontWeight: 'bold', color: '#000' }}
                        >{`Día${amountOfDays + 1 > 1 ? 's' : ''}:`}</span>
                        <span style={{ marginLeft: '4px' }}>{`${
                          amountOfDays + 1
                        } / `}</span>
                        <span
                          style={{ fontWeight: 'bold', color: '#000' }}
                        >{`Noche${amountOfDays + 1 > 1 ? 's' : ''}:`}</span>
                        <span
                          style={{ marginLeft: '4px' }}
                        >{`${amountOfDays}`}</span>
                      </span>
                    </span>
                  ) : (
                    ''
                  )}

                  <Button
                    style={{
                      backgroundColor: '#fff',
                      color: '#000',
                      border: '1px solid #ccc',
                      maxHeight: '36px',
                    }}
                    onClick={cleanDates}
                  >
                    Limpiar
                  </Button>
                  <Button
                    style={{
                      backgroundColor: theme.primary,
                      color: '#fff',
                      fontWeight: 'bold',
                      maxHeight: '36px',
                    }}
                    onClick={() => {
                      setIgnoreClose(true);
                      setHidden(true);
                      emitDateInfo(startDate, endDate);
                    }}
                  >
                    Aplicar
                  </Button>
                </div>
              )}
            </div>
          </ClickAwayListener>
        </div>
      </div>
    </Box>
  );
};

export default DateRangeGit;
